import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import React, { useEffect, useRef, useState } from "react";
import { Paper, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { navigateToDashboard } from "../../utils/navigation";
import { clearAuthToken } from "../../utils/authToken";
import axios from "axios";
import apiClient, { v1Backend } from "services/api/client";
import { useLocation } from "react-router-dom";
import useGetLoggedInUser from "services/hooks/LoggedInUser/useGetLoggedInUser";
import Header from "../Header/Header";
import sendEvents from "../../utils/sendEvents";
import useGetCompanyTypeData from "services/hooks/CompanyData/useGetCompanyTypeData";
import GlobalToast from "../Toasts/GlobalToast";
import { SidebarStyle } from "../../styles/sidebar.styles";
import UnifiedSidebarRoutes from "./UnifiedSidebarRoutes";
import PageLoader from "../LoadingButton/PageLoader";
import DashboardPageAccessGuard from "../DashboardPageAccessGuard";
import {
  isPublicBrowsePath,
  isMembershipFinalizingPath,
} from "../../utils/dashboardPageAccess";

const MenuIcon =
  "https://bxidevelopment1.s3.ap-south-1.amazonaws.com/assets/SidebarIcon/Menu.png";
const BXI_logo =
  "https://bxidevelopment1.s3.ap-south-1.amazonaws.com/assets/logos/bxi-logo-full.png";

export default function SidebarMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setState] = React.useState(false);
  const wrapperRef = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const {
    data: datahere,
    isLoading: DataLoading,
    error: DataError,
    isRefetching,
  } = useGetLoggedInUser();

  // Auto logout function - FIXED: Added missing function
  const autologOutUser = async (redirectPath = "/login") => {
    try {
      await axios.get("/auth/logout", { withCredentials: true });
      clearAuthToken(axios, v1Backend, apiClient);
      localStorage.clear();
      navigateToDashboard(redirectPath);
    } catch (err) {
      // Even if logout fails, clear local storage and redirect
      clearAuthToken(axios, v1Backend, apiClient);
      localStorage.clear();
      navigateToDashboard(redirectPath);
    }
    removeUserTypeOnLocalStorage();
  };

  // Logout function
  const logOutUser = async () => {
    const confirmed = window.confirm("Are you sure you want to logout?");
    if (confirmed === true) {
      try {
        const res = await axios.get("/auth/logout", { withCredentials: true });
        if (res.data === "Logged out" && res.status === 200) {
          clearAuthToken(axios, v1Backend, apiClient);
          localStorage.clear();
          navigateToDashboard("/login");
        }
      } catch (err) {
        console.error("Logout error:", err);
        // Even if logout fails, clear local storage and redirect
        clearAuthToken(axios, v1Backend, apiClient);
        localStorage.clear();
        navigateToDashboard("/login");
      }
      removeUserTypeOnLocalStorage();
    }
  };

  const removeUserTypeOnLocalStorage = () => {
    localStorage.removeItem("userhubtype");
  };

  // Get company type data
  const { data: companyTypeData } = useGetCompanyTypeData(
    datahere?.data?.companyType,
  );

  // Initialize user hub type from localStorage - FIXED: Added proper initialization
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Hard reload on home page after login (one time only)
  useEffect(() => {
    if (location.pathname === "/home") {
      const needsHardReload = localStorage.getItem("needsHardReload");
      if (needsHardReload === "true") {
        // Clear the flag first to prevent infinite reload
        localStorage.removeItem("needsHardReload");
        // Perform hard reload
        window.location.reload();
      }
    }
  }, [location.pathname]);

  // Update sidebar when pathname changes - FIXED: Added pathname listener to sync with Header
  useEffect(() => {
    if (!isInitialized) return;

    const updateHubType = () => {
      // Match the logic from Header.jsx to determine hub type based on pathname
      if (
        location.pathname === "/home" ||
        location.pathname === "/home/profile" ||
        location.pathname === "/home/cart" ||
        location.pathname === "/home/dashboard" ||
        location.pathname === "/home/trackorders" ||
        location.pathname === "/home/order-requests"
      ) {
        // Check if it's a buyer route (not seller-specific)
        if (location.pathname === "/home/trackorders") {
          // For shared routes, check localStorage to maintain current state
          const currentHubType = localStorage.getItem("userhubtype");
          if (currentHubType === "seller") {
            const hubType = "seller";
            localStorage.setItem("userhubtype", hubType);
            return;
          }
        }
        const hubType = "buyer";
        localStorage.setItem("userhubtype", hubType);
      } else if (
        location.pathname.startsWith("/home/seller") ||
        location.pathname.startsWith("/home/sellerorderlist") ||
        location.pathname.startsWith("/home/sellerdetailedordersummary") ||
        location.pathname.startsWith("/home/sellervoucherorderlist") ||
        location.pathname.startsWith("/home/sellerperformainvoice") ||
        location.pathname.startsWith("/home/sellerpilist") ||
        location.pathname.startsWith("/home/sellermediapo") ||
        location.pathname.startsWith("/home/orderdetailsseller") ||
        location.pathname.startsWith("/home/productanalytics")
      ) {
        const hubType = "seller";
        localStorage.setItem("userhubtype", hubType);
      } else if (
        location.pathname.startsWith("/home/order-requests") ||
        location.pathname.startsWith("/home/order-request/") ||
        location.pathname.startsWith("/home/myorderlist") ||
        location.pathname.startsWith("/home/voucherorderlist") ||
        location.pathname.startsWith("/home/wishlist") ||
        location.pathname.startsWith("/home/bulk-purchase") ||
        location.pathname.startsWith("/home/commission")
      ) {
      } else {
      }
    };

    updateHubType();
  }, [location.pathname, isInitialized]);

  // Handle data loading and error states - FIXED: Improved data handling logic
  useEffect(() => {
    if (!isInitialized) return;

    // If still loading, don't do anything
    if (DataLoading || isRefetching) {
      return;
    }

    // Check if user is on terms page - they should be allowed to stay even during data loading issues
    const isOnTermsPage =
      location.pathname === "/terms" || location.pathname.includes("/terms");

    // Marketplace landing + product/category pages are guest-viewable (shared allowlist).
    const isPublicPage = isPublicBrowsePath(location.pathname);

    // Juspay just returned the user here to finalize a plan they paid for.
    const isFinalizingMembership = isMembershipFinalizingPath(
      location.pathname,
      location.search,
    );

    // If there's an error or no data, logout user (except on terms page or product detail/category pages during initial load)
    if (DataError || !datahere?.data) {
      // Allow users on terms page or product detail/category pages to stay
      // This allows unauthenticated users to view product details and browse categories
      if (!isOnTermsPage && !isPublicPage) {
        GlobalToast("Please login again", "error");
        autologOutUser();
        return;
      }
      // If on terms page or product detail page but data error persists, we still need some data to proceed
      // So only return without logout, allowing retry
      return;
    }

    // Check if user should be logged out based on onboarding status
    // Allow users on terms page or product detail/category pages to stay
    if (!isOnTermsPage && !isPublicPage) {
      const invalidStatuses = [
        "UNVERIFIED",
        "GST",
        "BANK_DETAILS",
        "UNDER_REVIEW",
      ];

      if (invalidStatuses.includes(datahere?.data?.companyOnboardingStatus)) {
        GlobalToast("Account verification required", "error");
        autologOutUser();
        return;
      }
    }

    // Check membership expiry (skip for product detail/category pages to allow unauthenticated viewing)
    // Also skip on the post-payment finalizer: the plan is being created right now, so
    // the cached membership still reads as expired and logging the user out here would
    // abort finalization and bounce them back to the plans page.
    if (
      !isPublicPage &&
      !isFinalizingMembership &&
      datahere?.data?.memberships?.length > 0
    ) {
      const latestMembership =
        datahere.data.memberships[datahere.data.memberships.length - 1];
      if (
        latestMembership?.MembershipExpiryDate &&
        new Date().toISOString() > latestMembership.MembershipExpiryDate
      ) {
        GlobalToast("Your membership is expired please renew", "error");
        const expiryLogoutTimer = setTimeout(() => {
          autologOutUser("/renewal");
        }, 3000);
        // Cancel if the membership stops being expired (a renewal finished) before the
        // timer fires — otherwise a stale timer logs out a user who has just renewed.
        return () => clearTimeout(expiryLogoutTimer);
      }
    }
  }, [
    datahere,
    DataLoading,
    DataError,
    isRefetching,
    isInitialized,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Handle click outside to close sidebar
  useEffect(() => {
    function handleClickOutside(event) {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setState(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [wrapperRef]);

  const handleSidebarState = (props) => {
    setState(props);
  };

  // Show loading state while data is being fetched - FIXED: Added loading state
  if (!isInitialized || DataLoading) {
    return <PageLoader />;
  }

  // Marketplace landing + product/category pages are guest-viewable (shared allowlist).
  const isPublicPage = isPublicBrowsePath(location.pathname);
  // Product-detail pages still show the sidebar drawer for guests (category/index don't).
  const isProductDetailPage =
    location.pathname.includes("/productdetail/") ||
    location.pathname.includes("/voucherdetail/") ||
    location.pathname.includes("/giftandvaluevoucher/") ||
    location.pathname.includes("/electronicsdetail/") ||
    location.pathname.includes("/mediabuying/") ||
    location.pathname.includes("/multiplexmediabuying/") ||
    location.pathname.includes("/digitalAndHoardingMediaBuying/");

  // Don't show sidebar if no data or error - FIXED: Added error state handling
  // BUT allow product detail/category pages to render even without authentication
  if ((!datahere?.data || DataError) && !isPublicPage) {
    return (
      <Paper sx={SidebarStyle.PaperStyle} elevation={0}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
          }}
        >
          <Typography>Please login to continue</Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={SidebarStyle.PaperStyle} elevation={0}>
      {/* Show header if user is logged in, or if not on terms/public pages */}
      {/* If user is logged in, always show header (except on terms page) */}
      {location.pathname.includes("/terms") ||
      (isPublicPage && !datahere?.data) ? null : (
        <Header />
      )}
      {location.pathname.includes("/terms") ||
      (isPublicPage && !datahere?.data) ? null : (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: 1300,
            ...(state
              ? {
                  height: "100dvh",
                  maxHeight: "100dvh",
                  overflow: "hidden",
                }
              : {}),
            borderRadius: "0px 15px 15px 0px",
          }}
        >
          {/* Show sidebar if:
              1. User is logged in (show sidebar), OR
              2. User is not logged in but on product detail page (for viewing) */}
          {datahere?.data || isProductDetailPage ? (
            state ? (
              <div
                className="box"
                style={{
                  height: "100dvh",
                  maxHeight: "100dvh",
                  overflow: "hidden",
                }}
              >
                <Paper sx={SidebarStyle.MotionPaperStyle} ref={wrapperRef}>
                  <Button
                    onClick={() => {
                      setState(false);
                      sendEvents("Click on close menu");
                    }}
                    disableRipple
                    disableTouchRipple
                    disableFocusRipple
                    sx={{
                      ...SidebarStyle.CloseMenuButton,
                      display: {
                        xs: "flex",
                        sm: "flex",
                        md: "flex",
                      },
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 30,
                        fontWeight: 700,
                        cursor: "pointer",
                        lineHeight: 1,
                      }}
                    >
                      ×
                    </Typography>
                  </Button>

                  <Box sx={SidebarStyle.SidebarDrawerBody}>
                    <Box sx={SidebarStyle.SidebarDrawerHeader}>
                      <Box
                        onClick={() => {
                          navigate("/home");
                          setState(false);
                        }}
                        sx={SidebarStyle.SidebarBrandRow}
                      >
                        <Box
                          component="img"
                          src={BXI_logo}
                          alt="BXI Logo"
                          sx={SidebarStyle.SidebarBrandLogo}
                        />
                        <Typography
                          component="span"
                          sx={SidebarStyle.SidebarBrandText}
                        >
                          BARTER EXCHANGE OF INDIA
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={SidebarStyle.SidebarDrawerNav}>
                      {datahere?.data && (
                        <UnifiedSidebarRoutes
                          companyTypeData={companyTypeData}
                          sendDataToParent={handleSidebarState}
                        />
                      )}
                    </Box>

                    <Box sx={SidebarStyle.SidebarLogoutFooterStyle}>
                      <ButtonBase
                        disableRipple
                        sx={{
                          ...SidebarStyle.SidebarItemStyle,
                          ...SidebarStyle.SidebarLogoutButton,
                        }}
                        onClick={() => {
                          logOutUser();
                          sendEvents("Click on logout");
                        }}
                      >
                        <LogoutRoundedIcon />
                        Logout
                      </ButtonBase>
                    </Box>
                  </Box>
                </Paper>
              </div>
            ) : (
              <Button
                onClick={() => {
                  setState(true);
                  sendEvents("Click on menu button");
                }}
                disableRipple
                disableTouchRipple
                disableFocusRipple
                sx={{
                  position: "relative",
                  width: "50px",
                  height: "50px",
                  mt: 1,
                  "&:hover": {
                    backgroundColor: "transparent",
                    boxShadow: "none",
                  },
                  "&:active": {
                    boxShadow: "none",
                    backgroundColor: "transparent",
                  },
                }}
              >
                <Box
                  component={"img"}
                  src={MenuIcon}
                  alt="Menu"
                  sx={{
                    height: "auto",
                    width: {
                      xl: "80%",
                      lg: "80%",
                      md: "80%",
                      sm: "60%",
                      xs: "90%",
                    },
                    mt: { xl: 0, lg: 0, md: 0, sm: -2, xs: 0 },
                  }}
                />
              </Button>
            )
          ) : (
            // FIXED: Added proper error message when terms not accepted
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="error">
                Please accept terms and conditions to access the sidebar
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {location.pathname === "/home" ? (
        <Paper
          sx={{
            width: "100%",
            maxWidth: "100%",
            bgcolor: "#ffffff",
            borderRadius: 0,
            minHeight: "calc(100vh - 56px)",
            maxHeight: "calc(100vh - 56px)",
            overflowY: "auto",
            overflowX: "hidden",
          }}
          elevation={0}
        >
          <DashboardPageAccessGuard />
        </Paper>
      ) : (
        <Paper
          sx={SidebarStyle.OutLetParentStyle}
          elevation={0}
          id="main-scroll-container"
        >
          <Box sx={SidebarStyle.OutLetBoxStyle}>
            <DashboardPageAccessGuard />
          </Box>
        </Paper>
      )}
    </Paper>
  );
}